import FormControl, { type FormControlProps } from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import Tooltip, { type TooltipProps } from '@mui/material/Tooltip';
import { InfoIcon } from '@phosphor-icons/react/Info';
import { type JSX, type ReactNode, type RefObject, useRef } from 'react';
import { useResizeObserver } from 'usehooks-ts';

export type FormFieldWithInfoProps = {
  autoAlign?: boolean;
  dataTest?: string;
  info?: ReactNode;
  tooltipDataTest?: string;
  tooltipPlacement?: TooltipProps['placement'];
  tooltipTop?: string;
} & FormControlProps;

export function FormFieldWithInfo({
  autoAlign = false,
  children,
  dataTest,
  info,
  fullWidth = true,
  tooltipDataTest = dataTest && `${dataTest}-tooltip`,
  tooltipPlacement = 'right',
  tooltipTop,
  ...props
}: FormFieldWithInfoProps): JSX.Element | null {
  const ref = useRef<HTMLDivElement>(null);
  const iconRef = useRef<HTMLButtonElement>(null);

  useResizeObserver({
    // usehooks-ts@3.1.1's useResizeObserver still types `ref` as RefObject<T> (non-null), unlike React 19's useRef
    ref: ref as RefObject<HTMLDivElement>,
    onResize: () => {
      if (!autoAlign || !ref.current || !iconRef.current) {
        return;
      }

      const input = ref.current.querySelector<HTMLInputElement>('.MuiInputBase-root');
      if (!input) {
        return;
      }

      const top =
        input.offsetTop +
        input.getBoundingClientRect().height * 0.5 -
        iconRef.current.getBoundingClientRect().height * 0.5;

      iconRef.current.style.top = `${top}px`;
    },
  });

  return (
    <FormControl {...props} data-test={dataTest} ref={ref} fullWidth={fullWidth}>
      {children}
      {info && (
        <StyledTooltip data-test={tooltipDataTest} placement={tooltipPlacement} title={info} tooltipTop={tooltipTop}>
          <IconButton size="small" ref={iconRef}>
            <InfoIcon data-test="InfoRoundedIcon" />
          </IconButton>
        </StyledTooltip>
      )}
    </FormControl>
  );
}

const StyledTooltip = styled(Tooltip, { shouldForwardProp: prop => prop !== 'tooltipTop' })<{
  tooltipTop?: string;
}>`
  position: absolute;
  right: -32px;
  top: ${({ tooltipTop }) => tooltipTop ?? '28px'};
`;
